import { Suspense, lazy } from 'react';

import { RiArrowRightLine, RiPlayCircleLine } from '@remixicon/react';

import {
  type ToolGroupEntry,
  createToolExtension,
  mcpTool,
} from '@spotify/plugin-confidence-chat-react';

import { recordingsPlugin } from '../plugin';
import classes from './RecordingToolExtension.module.css';

const LazyRecordingPlayer = lazy(() =>
  import('@spotify/confidence-recordings-react').then(m => ({
    default: m.RecordingPlayer,
  })),
);

type RecordingHit = {
  name: string;
  display_name: string;
  score: number;
  link?: string;
  preview?: string;
};

type SearchRecordingsResult = {
  query: string;
  total: number;
  recordings: RecordingHit[];
};

const parseSearchResult = (
  entry: ToolGroupEntry,
): SearchRecordingsResult | null => {
  if (!entry.result?.content) return null;
  try {
    const parsed = JSON.parse(entry.result.content) as SearchRecordingsResult;
    if (Array.isArray(parsed.recordings)) return parsed;
  } catch {
    /* not valid JSON */
  }
  return null;
};

const getLabel = (recording: RecordingHit): string =>
  recording.display_name || recording.name;

const RecordingToolRenderer = ({ entry }: { entry: ToolGroupEntry }) => {
  const result = parseSearchResult(entry);
  const topRecording = result?.recordings.find(r => r.preview);

  if (!topRecording?.preview) return null;

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        {topRecording.link ? (
          <a
            href={topRecording.link}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.recordingLink}
          >
            {getLabel(topRecording)}
            <span className={classes.score}>
              ({(topRecording.score * 100).toFixed(1)}% relevance)
            </span>
            <RiArrowRightLine size={14} />
          </a>
        ) : (
          <span className={classes.recordingName}>
            {getLabel(topRecording)}
          </span>
        )}
      </div>
      <div className={classes.container}>
        <Suspense fallback={<div className={classes.placeholder} />}>
          <LazyRecordingPlayer
            src={topRecording.preview}
            variant="simplified"
            autoplay={false}
          />
        </Suspense>
      </div>
    </div>
  );
};

export const RecordingToolExtension = recordingsPlugin.provide(
  createToolExtension({
    id: 'recording-preview',
    predicate: mcpTool('semantic_search_recordings'),
    icon: RiPlayCircleLine,
    display: 'prominent',
    render: () => null,
    renderTool: entry => <RecordingToolRenderer entry={entry} />,
  }),
);
